import { defineStore } from 'pinia'
import { config } from "@/core/config"
import { SmartSearchConfig } from "@/main"
import { searchErrorLogger } from "@/core/Logger"
import { SanitizeValue } from "@/core/utils"

/**
 * Typ na poj. popular query:
 * odpowiada obiektom z kluczami "query" i "count"
 */
interface PopularQueryItem {
  query: string
  count: number
}

export const usePopularQueriesStore = defineStore('PopularQueriesStore', {
  state: () => ({
    // Tablica obiektów { query, count }
    queries: [] as PopularQueryItem[],
  }),
  
  getters: {
    hasPopularQueries(state) {
      return state.queries.length > 0
    },
    
    getPopularQueries(state) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const limit = SmartSearchConfig.value?.onfocus?.popularQueries?.count ?? 10
      return state.queries.slice(0, limit)
    },
  },
  
  actions: {
    /**
     * Generuje klucz w sessionStorage na podstawie searchId (np. "71ff880a9cc666ba..._popularQueries")
     */
    getSessionStorageKey(): string {
      const { searchId } = config
      return `${searchId}_popularQueries`
    },
    
    /**
     * Pobiera popular queries:
     * - najpierw próbuje odczytać z cache (sessionStorage),
     *   jeśli cokolwiek pójdzie nie tak (błąd parse, pusta tablica itp.),
     *   przechodzi do fetch z API
     */
    async fetchPopularQueries() {
      // 1) Sprawdzamy czy popular queries są włączone
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const popularQueriesEnabled = SmartSearchConfig.value?.onfocus?.popularQueries?.enabled
      if (!popularQueriesEnabled) {
        return []
      }
      
      const { apiHost, customerSymbol, searchId, apiKey } = config
      const sessionKey = this.getSessionStorageKey()
      
      // 2) Próba odczytu z sessionStorage
      const cachedData = sessionStorage.getItem(sessionKey)
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData)
          // Oczekujemy, że parsed to tablica
          if (Array.isArray(parsed) && parsed.length > 0) {
            // Sanityzacja
            const sanitized = parsed.map((obj: any) => ({
              query: SanitizeValue(obj.query || ''),
              count: obj.count ?? 0,
            }))
            
            if (sanitized.length > 0) {
              // Mamy niepustą tablicę, zapisujemy do store
              this.queries = sanitized
              return this.queries
            }
          }
          // Jeśli parsed jest pusty, lecimy do fetch (patrz sekcja 3)
        } catch (err) {
          // W razie jakiegokolwiek błędu parse:
          searchErrorLogger.log('Error reading popular queries from cache:', err)
        }
      }
      
      // 3) Jeśli brak (lub błąd parse, pusta tablica, cokolwiek), fetch z API
      try {
        const response = await fetch(
          `https://${apiHost}/restapi/${customerSymbol}/search/${searchId}/queries/popular`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Api-key': apiKey,
            },
          }
        )
        
        if (response.ok) {
          const data = await response.json()
          // data.queries => tablica obiektów { query, count }
          
          // sanityzacja
          const sanitizedQueries = (data.queries || []).map((obj: any) => ({
            query: SanitizeValue(obj.query || ''),
            count: obj.count ?? 0,
          }))
          
          // zapis w store
          this.queries = sanitizedQueries
          
          // zapis w sessionStorage
          sessionStorage.setItem(sessionKey, JSON.stringify(sanitizedQueries))
          return this.queries
        } else {
          // np. błąd 4xx/5xx => pusta tablica
          this.queries = []
          return []
        }
      } catch (error) {
        searchErrorLogger.log('Error retrieving popular queries:', error)
        this.queries = []
        return []
      }
    },
    
    /**
     * Czyści cache i store
     */
    clearPopularQueriesCache() {
      const sessionKey = this.getSessionStorageKey()
      sessionStorage.removeItem(sessionKey)
      this.queries = []
    },
  },
})
